
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { ClipboardCheck, CheckCircle, XCircle, ArrowRight } from 'lucide-react';

const QualityDashboard: React.FC = () => {
  const navigate = useNavigate();

  const { data: stats } = useQuery({
    queryKey: ['quality-dashboard-stats'],
    queryFn: async () => {
      const [pending, approved, rejected] = await Promise.all([
        supabase
          .from('procurements')
          .select('id', { count: 'exact' })
          .eq('status', 'pending_quality'),
        supabase
          .from('procurement_quality')
          .select('id', { count: 'exact' })
          .not('quality_approved_by', 'is', null),
        supabase
          .from('procurements')
          .select('id', { count: 'exact' })
          .eq('rejection_source', 'quality')
      ]);

      return {
        pending: pending.count || 0,
        approved: approved.count || 0,
        rejected: rejected.count || 0
      };
    }
  });

  return (
    <div className="space-y-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold">Quality Dashboard</h1>
        <p className="text-muted-foreground mt-1">
          Inspect and verify procurement quality before warehouse confirmation
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/quality')}>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Pending Inspection</CardTitle>
            <ClipboardCheck className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.pending || 0}</div>
            <p className="text-xs text-muted-foreground">awaiting quality check</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Approved</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{stats?.approved || 0}</div>
            <p className="text-xs text-muted-foreground">quality verified</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Rejected</CardTitle>
            <XCircle className="h-4 w-4 text-destructive" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-destructive">{stats?.rejected || 0}</div>
            <p className="text-xs text-muted-foreground">failed inspection</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <Button onClick={() => navigate('/quality')} className="w-full sm:w-auto">
            Go to Quality Inspection
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default QualityDashboard;
